import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

// Payout Service
export const payoutService = {
  // Save payout record
  async savePayout(payoutData) {
    try {
      const docRef = await addDoc(collection(db, 'payouts'), {
        ...payoutData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, payoutId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update payout status
  async updatePayoutStatus(payoutId, status, additionalData = {}) {
    try {
      const payoutRef = doc(db, 'payouts', payoutId);
      await updateDoc(payoutRef, {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get payout by ID
  async getPayout(payoutId) {
    try {
      const payoutRef = doc(db, 'payouts', payoutId);
      const payoutSnap = await getDoc(payoutRef);
      
      if (payoutSnap.exists()) {
        return { 
          success: true, 
          payout: { id: payoutSnap.id, ...payoutSnap.data() } 
        };
      } else {
        return { success: false, error: 'Payout not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get payouts for an entity
  async getEntityPayouts(entityType, entityId, limitCount = 50) {
    try {
      const q = query(
        collection(db, 'payouts'),
        where('entityType', '==', entityType),
        where('entityId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const payouts = [];
      
      querySnapshot.forEach((doc) => {
        payouts.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, payouts };
    } catch (error) {
      return { success: false, error: error.message, payouts: [] };
    }
  },

  // Create payout via API
  async createPayout(payoutData) {
    try {
      // Get gateway URL from environment or config
      const gatewayUrl = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000';
      
      const response = await fetch(`${gatewayUrl}/api/payment/create-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountNumber: payoutData.accountNumber,
          ifscCode: payoutData.ifscCode,
          amount: payoutData.amount,
          currency: payoutData.currency || 'INR',
          mode: payoutData.mode || 'NEFT',
          purpose: payoutData.purpose || 'settlement',
          referenceId: payoutData.referenceId,
          notes: {
            accountHolderName: payoutData.accountHolderName,
            entityType: payoutData.entityType,
            entityId: payoutData.entityId,
            entityName: payoutData.entityName,
            settlementId: payoutData.settlementId,
            period: payoutData.period
          }
        })
      });

      const result = await response.json();

      if (!result.success) {
        return { success: false, error: result.error || 'Failed to create payout' };
      }

      // Save payout record to Firebase
      const saveResult = await this.savePayout({
        razorpayPayoutId: result.payout.id,
        entityType: payoutData.entityType,
        entityId: payoutData.entityId,
        entityName: payoutData.entityName,
        amount: payoutData.amount,
        currency: payoutData.currency || 'INR',
        accountNumber: payoutData.accountNumber,
        ifscCode: payoutData.ifscCode,
        accountHolderName: payoutData.accountHolderName,
        status: result.payout.status,
        mode: result.payout.mode,
        utr: result.payout.utr,
        referenceId: result.payout.reference_id,
        settlementId: payoutData.settlementId,
        period: payoutData.period,
        notes: payoutData.notes || {}
      });

      return { 
        success: true, 
        payout: result.payout,
        payoutId: saveResult.payoutId
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check payout status
  async checkPayoutStatus(razorpayPayoutId) {
    try {
      const gatewayUrl = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000';
      
      const response = await fetch(`${gatewayUrl}/api/payment/payout/${razorpayPayoutId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (!result.success) {
        return { success: false, error: result.error || 'Failed to fetch payout status' };
      }

      // Update local payout record
      const payoutsQuery = query(
        collection(db, 'payouts'),
        where('razorpayPayoutId', '==', razorpayPayoutId)
      );
      const payoutsSnapshot = await getDocs(payoutsQuery);
      
      if (!payoutsSnapshot.empty) {
        const payoutDoc = payoutsSnapshot.docs[0];
        await updateDoc(doc(db, 'payouts', payoutDoc.id), {
          status: result.payout.status,
          utr: result.payout.utr,
          updatedAt: serverTimestamp()
        });
      }

      return { success: true, payout: result.payout };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

